<template>
  <main class="main-page-div" aria-label="Execute Test Runs Page">
    <v-container fluid class="redesign-main-page-container">
      <v-card class="main-content-card" elevation="0">
        <v-row no-gutters align="center" class="mb-3">
          <v-col cols="12" md="6">
            <h1 style="font-weight: 600; font-size: 28px;" class="mr-4">{{ $t("ExecuteTestRuns_title") }}</h1>
          </v-col>
          <v-col cols="12" md="6" class="d-flex flex-wrap justify-end gap-2">
            <!-- Search -->
            <div class="custom-search-input-wrapper">
              <div class="custom-search-input-container">
                <v-icon class="custom-search-input-icon">mdi-magnify</v-icon>
                <input id="Testname-field-search-validationProcess" type="text" :placeholder="$t('testName')"
                  :aria-label="$t('testName')" class="custom-search-input" v-model="searchName" @keydown.enter="Search" />
                <v-icon class="clear-icon" :style="searchName ? '' : 'color: transparent !important;'"
                  @click="searchName = ''">
                  mdi-close-circle
                </v-icon>
              </div>
            </div>
            <!-- Refresh Button -->
            <v-tooltip location="top" max-width="200">
              <template #activator="{ props }">
                <v-btn id="button-refresh-validationprocess" v-bind="props" icon
                  class="reportdata-icon-btn reportdata-icon-btn--ghost ml-2" variant="flat" @click="ManualRefresh()"
                  aria-label="Reset filters">
                  <v-icon size="24">mdi-restore</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("refresh") }}</span>
            </v-tooltip>
            <!-- Add Button -->
            <v-btn id="button-add-validationprocess" color="primary" class="control-btn create-btn" variant="elevated"
              size="large" prepend-icon="mdi-plus-circle" rounded="xl" @click="showPopup()">
              <span class="btn-text">{{ $t("add") }}</span>
              <v-tooltip activator="parent" location="top">
                {{ $t("_addNewQuestionSet") }}
              </v-tooltip>
            </v-btn>

          </v-col>
        </v-row>

        <v-data-table-server :items-per-page="options.itemsPerPage" :items-length="searchTotalTable"
          v-model:options="options" :items="dataTable" :headers="headers" :loading="loadingTable">
          <template v-slot:no-data>
            <div class="text-center py-2">
              <div class="text-h7 text-grey-darken-1 mt-4">{{ $t("_searchNotFound", { name: searchName }) }}
              </div>
            </div>
          </template>
          <template v-slot:item="{ item }">
            <tr>
              <td style="text-align: left">{{ item.number }}</td>
              <td style="text-align: left">{{ item.name }}</td>
              <td style="text-align: left">
                <span class="table-cell-text" :title="item.modelName || '-'">{{ item.modelName || "-" }}</span>
              </td>
              <td style="text-align: left">
                <v-chip v-if="item.questionType" :color="getQuestionTypeColor(item.questionType)" variant="tonal"
                  size="small" class="meta-chip">
                  {{ getQuestionTypeText(item.questionType) }}
                </v-chip>
                <span v-else>-</span>
              </td>
              <td style="text-align: left">
                <!-- Process Status with compact design -->
                <div class="status-container">
                  <v-chip :color="getProcessStatusColor(item)" variant="flat" size="small" class="status-chip">
                    <v-icon start size="14">
                      {{ getProcessStatusIcon(item) }}
                    </v-icon>
                    <span class="status-text">{{ getProcessStatusText(item) }}</span>
                  </v-chip>
                </div>
              </td>
              <td style="text-align: left">{{ item.createBy }}</td>
              <td style="text-align: left">{{ item.processingDate }}</td>
              <td style="text-align: center">
                <div class="action-container">
                  <div class="action-group">
                    <!-- Reprocess: ไม่ปิดตาม Processing เพราะ run ที่ค้างจะกู้ไม่ได้เลย
                         ปุ่มนี้มี confirm dialog อยู่แล้ว และการ reprocess จะสร้าง run ใหม่ให้ -->
                    <v-btn id="button-reprocess-validationprocess" variant="text" density="comfortable"
                      @click="Reprocess(item)" class="action-btn download-btn" icon>
                      <v-icon color="#FFA726" size="25">mdi-sync</v-icon>
                      <v-tooltip activator="parent" location="top">
                        {{ isRunning(item.status) ? $t("_reprocessRestartRun") : $t("reprocess") }}
                      </v-tooltip>
                    </v-btn>

                    <!-- Edit -->
                    <v-btn id="button-edit-popupedit-validationprocess" variant="text" density="comfortable"
                      @click="showPopup(item.processID)" class="action-btn edit-btn" icon
                      :disabled="item.status === 'Processing'">
                      <img :src="images.edit" alt="Edit" class="manage-image-icon" />
                      <v-tooltip activator="parent" location="top">
                        {{ $t("edit") }}
                      </v-tooltip>
                    </v-btn>

                    <!-- Download -->
                    <v-btn id="button-download-validationprocess" variant="text" density="comfortable"
                      @click="Download(item.processID, item.name, item.isGroup)" class="action-btn download-btn" icon
                      :disabled="item.status === 'Processing'">
                      <v-icon color="#2ABF56" size="25">mdi-tray-arrow-down</v-icon>
                      <v-tooltip activator="parent" location="top">
                        {{ $t("downloadResults") }}
                      </v-tooltip>
                    </v-btn>

                    <!-- Custom Divider -->
                    <div class="action-divider"></div>

                    <!-- Delete: ไม่ปิดตาม Processing ด้วยเหตุผลเดียวกับ Reprocess
                         มิฉะนั้น run ที่ค้างจะลบทิ้งไม่ได้ (มี confirm dialog อยู่แล้ว) -->
                    <v-btn id="button-delete-validationprocess" variant="text" density="comfortable"
                      @click="DeleteProcessPopup(item.processID, item.status)" class="action-btn delete-btn" icon>
                      <img :src="images.delete" alt="Delete" class="manage-image-icon" />
                      <v-tooltip activator="parent" location="top">
                        {{ $t("delete") }}
                      </v-tooltip>
                    </v-btn>
                  </div>
                </div>
              </td>
              <td style="text-align: center; padding: 8px">
                <v-btn id="button-score-validationprocess" icon variant="flat" size="large" class="score-btn"
                  @click="GoToScore(item.processID)" :disabled="item.status !== 'success'">
                  <v-img :src="images.chartImg" alt="Results" class="manage-image-icon"></v-img>
                  <v-tooltip activator="parent" location="top">
                    {{
                      item.status === "success"
                        ? $t("viewDashboardScore")
                        : $t("_completeProcessToViewScores")
                    }}
                  </v-tooltip>
                </v-btn>
              </td>
            </tr>
          </template>
          <template v-slot:bottom>
            <div class="table-bottom-group">
              <div class="d-flex justify-space-between align-center px-4 py-2" style="width: 100%">
                <div class="d-flex flex-column">
                  <span>{{ $t("totalResults") }} {{ searchTotalTable }}</span>
                </div>

                <v-data-table-footer :items-per-page-options="itemsPerPageOptions"
                  v-model:items-per-page="options.itemsPerPage" v-model:page="options.page" :length="pageCount" />
              </div>
            </div>
          </template>
        </v-data-table-server>
      </v-card>
    </v-container>

    <v-dialog ref="form" v-model="Uploaddialog" persistent max-width="900px" class="modern-dialog">
      <v-card class="dialog-card">
        <!-- Header Section -->
        <v-card-title class="dialog-header"
          :class="{ 'dialog-header-edit': processID, 'dialog-header-add': !processID }">
          <div class="header-main">
            <div class="header-text-content">
              <h2 class="dialog-title">
                {{
                  processID
                    ? $t("editValidationProcess")
                    : $t("uploadQuestionSet")
                }}
              </h2>
              <p class="dialog-subtitle">
                {{
                  processID
                    ? $t("_modifyValidationConfig")
                    : $t("_createValidationConfig")
                }}
              </p>
            </div>
          </div>
          <div class="header-actions">
            <v-btn icon variant="text" @click="closeUploaddialog" class="close-button" color="white">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-divider></v-divider>

        <!-- Content Section -->
        <v-card-text class="dialog-content">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row no-gutters>
              <!-- Row 1: Test Name & Select Model -->
              <v-col cols="12" sm="12" md="6">
                <div class="d-flex flex-column w-100 pr-5">
                  <label class="input-label" for="testname-field-validationprocess">
                    {{ $t("testName") }} <span class="required-asterisk">*</span>
                    <v-tooltip location="top" max-width="300">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="18" class="info-icon" color="grey-darken-1">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ $t("_testNameTooltip") }}</span>
                    </v-tooltip>
                  </label>

                  <v-text-field id="testname-field-validationprocess" :placeholder="$t('_enterTestName')"
                    v-model="Validation.validationName" :rules="[rules.required]" variant="outlined"
                    density="comfortable" color="#a0a0a0" class="modern-field" clearable hide-details="auto" required>
                  </v-text-field>
                </div>
              </v-col>

              <v-col cols="12" sm="12" md="6">
                <div class="d-flex flex-column w-100 pl-md-5">
                  <label class="input-label" for="modelname-autocomplete-validationprocess">
                    {{ $t("selectModel") }}
                    <v-tooltip location="top" max-width="300">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="18" class="info-icon" color="grey-darken-1">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ $t("_selectModelTooltip") }}</span>
                    </v-tooltip>
                  </label>

                  <v-autocomplete id="modelname-autocomplete-validationprocess" :placeholder="$t('_selectModels')"
                    :items="DDGPTModel" item-title="name" item-value="modelDisplayID" variant="outlined"
                    density="comfortable" color="#a0a0a0" class="modern-field" clearable hide-details="auto"
                    v-model="Validation.modelId" :rules="[rules.required]" required>
                    <template v-slot:item="{ props, item, index }">
                      <v-list-item v-bind="props" :id="'modelname-option-' + index" :value="item.raw.name">
                        <template v-slot:title>
                          <span>{{ item.raw.name }}</span>
                        </template>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>

              <!-- Row 2: Question Type & Processing -->
              <v-col cols="12" sm="12" md="6" class="mt-4">
                <div class="d-flex flex-column w-100 pr-5">
                  <label class="input-label" for="questiontype-radiogroup-validationprocess">
                    {{ $t("questionType") }}
                    <v-tooltip location="top" max-width="300">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="18" class="info-icon" color="grey-darken-1">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ $t("_questionTypeTooltip") }}</span>
                    </v-tooltip>
                  </label>

                  <v-radio-group id="questiontype-radiogroup-validationprocess" v-model="Validation.positive" @change="clearRadio()" class="custom-radio-group mt-2"
                    inline hide-details color="#C32E0E">
                    <v-radio id="positive-radio-validationprocess" :label="$t('positive')" :value="true"
                      :disabled="!QuestionType" color="#C32E0E" class="custom-radio">
                    </v-radio>

                    <v-radio id="negative-radio-validationprocess" :label="$t('negative')" :value="false"
                      :disabled="!QuestionType" color="#C32E0E" class="custom-radio">
                    </v-radio>
                  </v-radio-group>
                </div>
              </v-col>

              <v-col cols="12" sm="12" md="6" class="mt-4">
                <div class="d-flex flex-column w-100 pl-md-5">
                  <label class="input-label" for="processing-radiogroup-validationprocess">
                    {{ $t("processing") }}
                    <v-tooltip location="top" max-width="300">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="18" class="info-icon" color="grey-darken-1">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ $t("_processingTooltip") }}</span>
                    </v-tooltip>
                  </label>

                  <div class="d-flex align-center gap-3 mt-2">
                    <v-radio-group id="processing-radiogroup-validationprocess" v-model="Validation.isGroup" @change="ChainRadio()" :disabled="!QuestionType"
                      class="custom-radio-group" inline hide-details color="#C32E0E">
                      <v-radio id="chain-radio-validationprocess" :label="$t('chain')" :value="true" color="#C32E0E"
                        class="custom-radio">
                      </v-radio>
                    </v-radio-group>

                    <v-btn v-if="Validation.isGroup" id="downloadtemplate-validationprocess" variant="outlined"
                      size="small" prepend-icon="mdi-tray-arrow-down" @click="chainTemplate()" class="template-btn">
                      {{ $t("template") }}
                    </v-btn>
                  </div>
                </div>
              </v-col>

              <!-- Row 3: Question Set Name & Upload File -->
              <v-col cols="12" sm="12" md="6" class="mt-4">
                <div class="d-flex flex-column w-100 pr-5">
                  <label class="input-label" for="questionsetname-autocomplete-validationprocess">
                    {{ $t("questionSetName") }}
                    <v-tooltip location="top" max-width="300">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="18" class="info-icon" color="grey-darken-1">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span v-if="Validation.positive">{{ $t("_InfoQuestionPositiveName") }}</span>
                      <span v-else-if="Validation.isGroup">{{ $t("_InfoQuestionPositiveName") }}</span>
                      <span v-else>{{ $t("_InfoQuestionNegativeName") }}</span>
                    </v-tooltip>
                  </label>

                  <!-- Positive Questions -->
                  <v-autocomplete v-if="Validation.positive" id="questionsetname-autocomplete-validationprocess"
                    :placeholder="$t('_selectPositiveQuestionSet')" :items="DDGenerateQ" item-title="name"
                    item-value="generateID" variant="outlined" density="comfortable" color="#a0a0a0"
                    class="modern-field" clearable hide-details="auto" v-model="Validation.generateId"
                    :disabled="Validation.entity != null || Validation.isGroup">
                    <template v-slot:item="{ props, item, index }">
                      <v-list-item v-bind="props" :id="'questionsetname-positive-option-' + index"
                        :value="item.raw.name">
                        <template v-slot:title>
                          <span>{{ item.raw.name }}</span>
                        </template>
                      </v-list-item>
                    </template>
                  </v-autocomplete>

                  <!-- Chain Questions -->
                  <v-autocomplete v-else-if="Validation.isGroup" id="chainquestion-autocomplete-validationprocess"
                    :placeholder="$t('_selectChainQuestion')" :items="chainQuestionList" item-title="name"
                    item-value="generateID" variant="outlined" density="comfortable" color="#a0a0a0"
                    class="modern-field" clearable hide-details="auto" v-model="Validation.generateId">
                    <template v-slot:item="{ props, item, index }">
                      <v-list-item v-bind="props" :id="'chainquestion-chain-option-' + index" :value="item.raw.name">
                        <template v-slot:title>
                          <span>{{ item.raw.name }}</span>
                        </template>
                      </v-list-item>
                    </template>
                  </v-autocomplete>

                  <!-- Negative Questions -->
                  <v-autocomplete v-else id="questionnegativename-autocomplete-validationprocess"
                    :placeholder="$t('_selectNegativeQuestionSet')" :items="DDValidationNegative" variant="outlined"
                    density="comfortable" color="#a0a0a0" class="modern-field" clearable hide-details="auto"
                    v-model="Validation.negativeName" :disabled="Validation.entity != null || Validation.isGroup">
                    <template v-slot:item="{ props, item, index }">
                      <v-list-item v-bind="props" :id="'questionsnegative-negative-option-' + index" :value="item.raw">
                        <template v-slot:title>
                          <span>{{ item.raw }}</span>
                        </template>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>

              <v-col cols="12" sm="12" md="6" class="mt-4">
                <div class="d-flex flex-column w-100 pl-md-5">
                  <span class="input-label">
                    {{ $t("uploadFile") }}
                    <v-tooltip location="top" max-width="300">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="18" class="info-icon" color="grey-darken-1">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ $t("_uploadExcelTooltip") }}</span>
                    </v-tooltip>
                  </span>

                  <v-card class="upload-card" elevation="0">
                    <v-card-text class="d-flex flex-column align-center justify-center pa-6">
                      <DragDropFile :dialog="true" :DragDrop="false" :ShowListFile="false"
                        :DisabledBtn="Validation.generateId != null || Validation.negativeName != null"
                        :allowType="['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']"
                        @filesUploaded="processUpload($event)" class="UploadFile" />
                      <div class="text-caption text-grey-darken-1 mt-2">
                        <v-icon size="16" class="mr-1">mdi-file-excel-outline</v-icon>
                        {{ $t("_supportFormatsXlsx") }}
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>

              <!-- Uploaded Files Display -->
              <v-col cols="12" sm="12" md="12" v-show="Validation.entity != null" class="mt-4">
                <div class="field-container">
                  <v-card v-if="uploadedFiles.length > 0" class="files-card" elevation="2" rounded="lg">
                    <v-virtual-scroll :items="uploadedFiles" :height="Math.min(uploadedFiles.length * 60, 120)"
                      :item-height="60">
                      <template v-slot:default="{ item }">
                        <v-list-item class="file-item px-4 py-2">
                          <template v-slot:prepend>
                            <v-avatar size="36" color="success" class="mr-3">
                              <v-icon color="white" size="18">mdi-file-document</v-icon>
                            </v-avatar>
                          </template>

                          <v-list-item-title class="text-subtitle-2 font-weight-medium">
                            {{ item.name }}
                          </v-list-item-title>

                          <v-list-item-subtitle class="text-caption">
                            {{ item.size }} bytes
                          </v-list-item-subtitle>

                          <template v-slot:append>
                            <v-btn @click.stop="removeFile(item.name)" icon="mdi-close" size="small" variant="text"
                              color="error" class="ml-2" />
                          </template>
                        </v-list-item>
                        <v-divider class="mx-4" v-if="
                          uploadedFiles.indexOf(item) <
                          uploadedFiles.length - 1
                        " />
                      </template>
                    </v-virtual-scroll>
                  </v-card>
                </div>
              </v-col>

              <!-- Calculate Scores Checkbox -->
              <v-col cols="12" sm="12" md="12" class="mt-4">
                <div class="border-section-wrapper">
                  <v-checkbox id="calculatescores-checkbox-validationprocess" v-model="Validation.isScore"
                    color="#C32E0E" hide-details>
                    <template v-slot:label>
                      <div class="checkbox-label-wrapper">
                        <span>{{ $t("calculateScores") }}</span>
                        <v-tooltip location="top" max-width="300px">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" color="grey-darken-1" size="small" class="ml-1">
                              mdi-information-outline
                            </v-icon>
                          </template>
                          <span>{{ $t("_calculateScoresTooltip") }}</span>
                        </v-tooltip>
                      </div>
                    </template>
                  </v-checkbox>
                </div>
              </v-col>

              <!-- Prompt Textarea -->
              <v-col cols="12" sm="12" md="12" v-show="Validation.isScore" class="mt-4">
                <div class="d-flex flex-column w-100">
                  <label class="input-label" for="prompt-textarea-validationprocess">
                    {{ $t("prompt") }}
                    <v-tooltip location="top" max-width="300">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="18" class="info-icon" color="grey-darken-1">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{ $t("_promptTooltip") }}</span>
                    </v-tooltip>
                  </label>

                  <v-textarea id="prompt-textarea-validationprocess" variant="outlined" rows="8" auto-grow counter color="#a0a0a0" class="modern-textarea"
                    v-model="Validation.Prompt" hide-details required>
                  </v-textarea>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Actions Section -->
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" color="grey" @click="PrepareCancel" class="dialog-action-btn cancel-btn">
            {{ $t("cancel") }}
          </v-btn>
          <v-btn id="button-save-popup-validationprocess" variant="flat" size="large" @click="Save"
            class="dialog-action-btn" :class="{
              'save-btn-edit': processID,
              'save-btn-add': !processID,
            }">
            <v-img :src="images.newSaveImg" alt="Change Password Dialog" class="image-icon-style"></v-img>
            {{ ProcessID ? $t("edit") : $t("save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :model-value="dialog" class="align-center justify-center" persistent>
      <v-card class="loading-card" elevation="8">
        <v-card-text class="text-center">
          <v-progress-circular indeterminate size="64" color="primary" class="mb-4"></v-progress-circular>
          <h3 class="loading-title">{{ $t("loading") }}</h3>
          <p class="loading-subtitle">{{ $t("pleaseWait") }}</p>
        </v-card-text>
      </v-card>
    </v-overlay>
  </main>
</template>
<script>
import DragDropFile from "@/components/DragDropFile.vue";
import Cookies from "js-cookie";
import ExcelJS from "exceljs";
import editImg from "@/assets/Icons/Edit.png";
import deleteImg from "@/assets/Icons/Delete.png";
import chartImg from "@/assets/Icons/chart-bold.svg";
import deleteIcon from "@/assets/images/trash-solid.svg";
import newSaveImg from "@/assets/Icons/white-save-floppy.svg";
import validationRunStatusMixin from "@/mixins/validationRunStatusMixin";

export default {
  name: "ValidationProcess",
  emits: ["filesUploaded"],
  inject: ["flagNav", "FontColorTitle", "SubNavbarBackGround"],
  components: { DragDropFile },
  mixins: [validationRunStatusMixin],
  data() {
    return {
    userId: Cookies.get("userId"),
    searchName: "",
    ProcessID: null,
    valid: true,
    checkbox1: false,
    dialog: false,
    Uploaddialog: false,
    today: null,
    searchTotalTable: 0,
    totalTable: 0,
    itemsPerPage: 10,
    isFirst: true,
    loadingTable: true,
    DeleteProcessPopUp: false,
    QuestionType: null,
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
    },
    dataTable: [],
    DDGPTModel: [],
    DDGenerateQ: [],
    chainQuestionList: [],
    DDValidationNegative: [],
    DDValidationPositive: [],
    uploadedFiles: [],
    DDProvider: [
      "Azure",
      "Azure Postgresql",
    ],
    Validation: {
      validationName: null,
      modelId: null,
      positive: true,
      negative: false,
      positiveUploadFlag: true,
      generateId: null,
      positiveName: null,
      negativeName: null,
      isScore: true,
      isGroup: false,
      entity: null,
      Prompt: null,
    },
    rules: {
      required: (value) => !!value || this.$t("Required"),
    },
    sortBy: [],
    sortDesc: [],
    autoRefreshRunning: false,
    autoRefreshStopped: false,
    // ตัวนับลำดับ request ของตาราง — Get() (ผู้ใช้เปลี่ยนหน้า/ค้นหา/เรียง) กับ ReFreshGet() (polling ทุก 5 วิ)
    // เขียน dataTable ตัวเดียวกันโดยไม่มีการยกเลิก response เก่า ถ้า response ของ poll หน้าเดิม
    // มาถึงหลัง response ของหน้าใหม่ ตารางจะถูกวาดกลับเป็นหน้าเก่าทั้งที่ footer บอกหน้าใหม่
    tableReqSeq: 0,

    images: {
      edit: editImg,
      delete: deleteImg,
      chartImg: chartImg,
      newSaveImg: newSaveImg,
    }
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t("no"), align: "left", sortable: false },
        { title: this.$t("testName"), align: "left", sortable: true, value: "Name" },
        // สองคอลัมน์นี้ sortable: false เพราะ backend เรียงได้เฉพาะคอลัมน์ที่ ApplyValidationProcessSorting รู้จัก
        // และ ModelID ที่เก็บไว้เป็น id ไม่ใช่ชื่อ การเรียงด้วยมันจึงไม่ตรงกับสิ่งที่ผู้ใช้เห็น
        { title: this.$t("model"), align: "left", sortable: false },
        { title: this.$t("questionType"), align: "left", sortable: false },
        {
          title: this.$t("processStatus"),
          align: "left",
          sortable: true,
          value: "ImportedDataCount",
        },
        {
          title: this.$t("createdBy"),
          align: "left",
          sortable: true,
          value: "CreateBy",
        },
        {
          title: this.$t("dataProcessed"),
          align: "left",
          sortable: true,
          value: "ProcessingDate",
        },
        { title: this.$t("manage"), align: "center", sortable: false },
        { title: this.$t("result"), align: "left", sortable: false },
      ];
    },
  },
  watch: {
    "Validation.isGroup"(newVal) {
      if (newVal === true) {
        this.Validation.positive = null;
      }
    },
    options: {
      handler() {
        this.Get();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    validate() {
      this.$refs.form.validate();
    },
    Refresh() {
      this.EditdialogUser = false;
    },
    Search() {
      this.isSearch = true;
      this.Get();
    },
    // isRunning / errorRowsOf / totalRowsOf / hasFailedRows / allRowsFailed
    // และ getQuestionType* / getProcessStatus* ย้ายไป validationRunStatusMixin
    // เพราะ panel บนหน้า /GPTModelDetail ต้องแปลสถานะของ run ให้ตรงกับตารางนี้เป๊ะ ๆ
    PrepareCancel() {
      this.$swal.fire({
        title: this.$t("_unsavedChanges"),
        icon: "warning",
        html: this.$t("_unsavedChangesBody"),
        showCloseButton: true,
        showCancelButton: true,
        cancelButtonText: this.$t("cancel"),
        confirmButtonText: this.$t("yes"),
        reverseButtons: true,
        focusCancel: true,
        buttonsStyling: false,
        allowOutsideClick: false,
        customClass: {
          popup: "swal-warning-popup",
          title: "swal-warning-title",
          htmlContainer: "swal-warning-body",
          actions: "swal-warning-actions",
          confirmButton: "swal-warning-confirm",
          cancelButton: "swal-warning-cancel",
          closeButton: "swal-warning-close",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          // User clicked "Yes" - proceed to close and clear data
          this.closeUploaddialog();
        }

      });

    },
    closeUploaddialog() {
      this.ProcessID = null;
      this.Uploaddialog = false;
      this.uploadedFiles = [];
      this.DeleteProcessPopUp = false;
      this.Validation.entity = null;
      this.Validation.validationName = null;
      this.Validation.modelId = null;
      this.Validation.positive = true;
      this.Validation.positiveFlag = null;
      this.Validation.generateId = null;
      this.Validation.PositiveName = null;
      this.Validation.negativeName = null;
      this.Validation.PositiveUploadFlag = null;
      this.Validation.isScore = true;
      this.Validation.isGroup = false;
    },
    clearRadio() {
      this.Validation.generateId = null;
      this.Validation.negativeName = null;
      this.Validation.isGroup = false;
    },
    ChainRadio() {
      this.Validation.positive = null;
      this.Validation.generateId = null;
      this.Validation.negativeName = null;
    },
    Get() {
      this.loadingTable = true;
      const myReq = ++this.tableReqSeq;

      let { page, itemsPerPage, sortBy } = this.options;
      let sortField = null;
      let sortOrder = null;

      if (sortBy && sortBy.length > 0) {
        sortField = sortBy[0].key;
        sortOrder = sortBy[0].order === "desc";
      }
      let temp = {
        name: this.searchName,
        page: this.isSearch ? 1 : page,
        perPage: itemsPerPage != -1 ? itemsPerPage : this.totalTable, // NOSONAR
        sortDesc: sortOrder,
        sortBy: sortField,
      };
      this.$call.GetTableValidationProcess(
        temp,
        (dataResponse) => {
          // มี request ที่ใหม่กว่าออกไปแล้ว → ทิ้ง response นี้ อย่าวาดทับของใหม่
          if (myReq !== this.tableReqSeq) return;
          if (dataResponse.status == 0) {
            this.searchTotalTable = dataResponse.data.total;
            this.dataTable = dataResponse.data.data;
            this.totalTable = this.isFirst
              ? dataResponse.data.total
              : this.totalTable;
            // เดิม guard ด้วย this.sortDesc == null แต่ sortDesc ถูก init เป็น [] จึงไม่เคยเป็น null
            // ทำให้ polling ไม่เคยเริ่มจาก Get() เลย เข้าถึงได้ทางเดียวคือหลังกด Reprocess
            this.AutoRefresh();
          } else {
            this.dataTable = [];
            this.searchTotalTable = 0;
            this.totalTable = 0;
          }
          this.loadingTable = false;
          this.isSearch = false;
          this.isFirst = false;
        },
        (errorResource) => {
          if (myReq !== this.tableReqSeq) return;
          // เดิมเซ็ต this.loading ซึ่งไม่มีใน data() จึงเป็น no-op และ loadingTable ค้าง true ตลอดไป
          // ตารางจะโชว์ loading ค้างเมื่อโหลดล้มเหลว
          this.loadingTable = false;
          this.isSearch = false;
          console.error(errorResource);
        }
      );
    },
    GetDetail(id) {
      this.dialog = true;
      this.loading = true;
      this.$call.GetValidationProcessDetail(
        id,
        (dataResponse) => {
          if (dataResponse.status == 0) {
            let item = dataResponse.data;
            this.Validation.validationName = item.name;
            this.Validation.modelId = item.modelID == -1 ? null : item.modelID;
            this.Validation.isGroup = item.isGroup;
            this.Validation.positive = item.positiveFlag;
            this.Validation.positiveUploadFlag = item.positiveUploadFlag;
            this.Validation.negativeFlag = item.negativeFlag;
            this.Validation.generateId =
              item.positiveName != null ? item.positiveName : item.generateID; // NOSONAR
            this.Validation.isScore = item.isScore;
            this.Validation.positiveName = item.positiveName;
            this.Validation.negativeName = item.negativeName;
            this.Validation.Prompt = item.prompt;
            this.dialog = false;
            this.Uploaddialog = true;
            this.loading = false;
          }
        },
        (errorResource) => {
          this.dialog = false;
          this.isSearch = false;
          this.loading = false;
          console.error(errorResource);
        }
      );
    },
    ReFreshGet() {
      let { page, itemsPerPage, sortBy } = this.options;
      let sortField = null;
      let sortOrder = null;

      if (sortBy && sortBy.length > 0) {
        sortField = sortBy[0].key;
        sortOrder = sortBy[0].order === "desc";
      }
      let temp = {
        name: this.searchName,
        page: this.isSearch ? 1 : page,
        perPage: itemsPerPage != -1 ? itemsPerPage : this.totalTable, // NOSONAR
        sortDesc: sortOrder,
        sortBy: sortField,
      };
      // อ่านค่าปัจจุบันเฉย ๆ ห้าม ++ ตรงนี้
      // การ guard ต้องเป็นทางเดียว: Get() ของผู้ใช้ทำให้ poll เก่าตกไปได้ แต่ poll ต้องไม่ทำให้ Get() ตกไป
      // ถ้า poll bump ตัวนับด้วย response ของ Get() ที่ยังค้างอยู่จะถูกทิ้ง แล้ว loadingTable = false
      // ที่อยู่ใน callback ของ Get() จะไม่ถูกเรียก → overlay loading ค้างหมุนถาวร
      const myReq = this.tableReqSeq;
      // คืน Promise เพื่อให้ AutoRefresh รอผลได้ ไม่ยิงซ้อนกัน และรู้ว่าล้มเหลวเมื่อไร
      return new Promise((resolve) => {
        this.$call.GetTableValidationProcess(
          temp,
          (dataResponse) => {
            // ผู้ใช้เปลี่ยนหน้า/ค้นหาระหว่างที่ poll นี้ยังค้างอยู่ → ทิ้งผล ไม่วาดทับ
            // แต่ยัง resolve ตามสถานะจริง เพื่อให้ตัวนับความล้มเหลวของ AutoRefresh ยังถูกต้อง
            if (myReq === this.tableReqSeq) {
              if (dataResponse.status == 0) {
                this.searchTotalTable = dataResponse.data.total;
                this.dataTable = dataResponse.data.data;
                this.totalTable = this.isFirst
                  ? dataResponse.data.total
                  : this.totalTable;
              } else {
                this.dataTable = [];
                this.searchTotalTable = 0;
                this.totalTable = 0;
              }
              this.isSearch = false;
              this.isFirst = false;
            }
            resolve(dataResponse.status == 0);
          },
          (errorResource) => {
            if (myReq === this.tableReqSeq) {
              this.isSearch = false;
            }
            console.error(errorResource);
            resolve(false);
          }
        );
      });
    },
    GetDDGPTModel() {
      this.loadingTable = true;
      this.$call.DDGPTModel(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.DDGPTModel = dataResponse.data;
          } else {
            this.DDGPTModel = [];
          }
          this.GetValidationGenerateDD();
        },
        (errorResource) => {
          this.loadingTable = false;
          console.error(errorResource);
        }
      );
    },
    GetValidationGenerateDD() {
      this.loadingTable = true;
      this.$call.GetValidationGenerateDD(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.DDGenerateQ = dataResponse.data;
          } else {
            this.DDGenerateQ = [];
          }
          this.GetValidationPositiveDD();
          this.GetValidationNegativeDD();
          this.loadingTable = false;
        },
        (errorResource) => {
          this.loadingTable = false;
          console.error(errorResource);
        }
      );
    },
    GetValidationNegativeDD() {
      this.dialog = true;
      this.$call.GetValidationNegativeDD(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.DDValidationNegative = dataResponse.data;
          } else {
            this.DDValidationNegative = [];
          }
          this.dialog = false;
        },
        (errorResource) => {
          this.dialog = false;
          console.error(errorResource);
        }
      );
    },
    GetValidationPositiveDD() {
      this.dialog = true;
      this.$call.GetValidationPositiveDD(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.DDValidationPositive = dataResponse.data;
            this.DDGenerateQ = this.DDGenerateQ.concat(
              dataResponse.data
            ).filter(
              (element) =>
                element.questionType == "P" || element.questionType == null
            );
            this.chainQuestionList = this.DDValidationPositive.filter(
              (element, index, selfArray) =>
                element.questionType == "C" &&
                index ===
                selfArray.findIndex(
                  (e) => e.name.trim() === element.name.trim()
                )
            );
          } else {
            this.DDValidationPositive = [];
          }
          this.dialog = false;
        },
        (errorResource) => {
          this.dialog = false;
          console.error(errorResource);
        }
      );
    },
    Save() {
      if (this.processID == null) {
        this.SaveAdd();
      } else {
        this.SaveEdit(this.processID);
      }
    },
    SaveAdd() {
      setTimeout(() => {
        this.validate();
      }, 100);
      if (
        this.Validation.validationName == null ||
        this.Validation.validationName == "" ||
        this.Validation.modelId == null ||
        this.Validation.modelId == "" ||
        (this.Validation.entity == null &&
          this.Validation.generateId == null &&
          this.Validation.negativeName == null &&
          this.Validation) ||
        (this.Validation.isScore &&
          (this.Validation.Prompt == null ||
            this.Validation.Prompt == "" ||
            this.Validation.Prompt == undefined))
      ) {
        // use this.$swal.fire to show an alert
        this.$swal.fire({
          title: this.$t("_IncompleteInformation"),
          icon: "warning",
          html: this.$t("_completeAllFields"),
          showCloseButton: true,
          focusConfirm: true,
          buttonsStyling: false,
          allowOutsideClick: false,
          confirmButtonText: this.$t("ok"),
          customClass: {
            popup: "swal-warning-popup",
            title: "swal-warning-title",
            htmlContainer: "swal-warning-body",
            actions: "swal-warning-actions",
            confirmButton: "swal-warning-confirm",
            closeButton: "swal-warning-close",
          },
        });
        return;
      }
      this.dialog = true;
      let request = {
        Name: this.Validation.validationName,
        ModelID: this.Validation.modelId,
        PositiveFlag:
          this.Validation.isGroup &&
            this.Validation.PositiveName == null
            ? false
            : this.Validation.positive,
        NegativeFlag:
          this.Validation.isGroup ? false : !this.Validation.positive,
        PositiveUploadFlag:
          this.Validation.isGroup ? false : this.Validation.positive,
        GenerateID:
          typeof this.Validation.generateId === "string"
            ? null
            : this.Validation.generateId,
        PositiveName:
          typeof this.Validation.generateId === "string"
            ? this.Validation.generateId
            : null,
        NegativeName: this.Validation.negativeName,
        IsScore: this.Validation.isScore,
        IsGroup: this.Validation.isGroup,
        Prompt: this.Validation.Prompt,
      };
      try {
        this.$call.CreateValidationProcess(
          request,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              if (dataResponse.data != 0) { // NOSONAR
                this.Processing(dataResponse.data);
              } else {
                this.$swal.fire({
                  title: this.$t("_questionSetCreatedSuccess"),
                  showConfirmButton: false,
                  timer: 1500,
                  customClass: {
                    popup: 'success-banner',
                    title: 'success-banner-title'
                  },
                });
              }
              this.totalTable = this.totalTable + 1;
              this.Get();
              this.closeUploaddialog();
            } else if (dataResponse.status == 3) {
              this.$swal.fire({
                icon: "warning",
                title: dataResponse.message,
                showConfirmButton: true,
              });
            }
            this.dialog = false;
          },
          (errorResource) => {
            this.dialog = false;
            this.Adddialog = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "DeleteGroup");
      }
    },

    SaveEdit(id) {
      setTimeout(() => {
        this.validate();
      }, 100);
      if (
        this.Validation.validationName == null ||
        this.Validation.validationName == "" ||
        this.Validation.modelId == null ||
        this.Validation.modelId == "" ||
        (this.Validation.entity == null &&
          this.Validation.generateId == null &&
          this.Validation.negativeName == null &&
          this.Validation)
      ) {
        this.$swal.fire({
          title: this.$t("_IncompleteInformation"),
          icon: "warning",
          html: this.$t("_completeAllFields"),
          showCloseButton: true,
          focusConfirm: true,
          buttonsStyling: false,
          allowOutsideClick: false,
          confirmButtonText: this.$t("ok"),
          customClass: {
            popup: "swal-warning-popup",
            title: "swal-warning-title",
            htmlContainer: "swal-warning-body",
            actions: "swal-warning-actions",
            confirmButton: "swal-warning-confirm",
            closeButton: "swal-warning-close",
          },
        });
        return;
      }
      this.dialog = true;
      let request = {
        ProcessID: id,
        Name: this.Validation.validationName,
        ModelID: this.Validation.modelId,
        PositiveFlag:
          this.Validation.isGroup ? false : this.Validation.positive,
        NegativeFlag:
          this.Validation.isGroup ? false : !this.Validation.positive,
        PositiveUploadFlag:
          this.Validation.isGroup ? false : this.Validation.positive,
        GenerateID:
          typeof this.Validation.generateId === "string"
            ? null
            : this.Validation.generateId,
        PositiveName:
          typeof this.Validation.generateId === "string"
            ? this.Validation.generateId
            : null,
        NegativeName: this.Validation.negativeName,
        IsGroup: this.Validation.isGroup,
        IsScore: this.Validation.isScore,
        Prompt: this.Validation.Prompt,
      };
      try {
        this.$call.EditValidationProcess(
          request,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              if (dataResponse.data != 0) { // NOSONAR
                this.Processing(dataResponse.data);
              } else {
                this.$swal.fire({
                  title: this.$t("_successfullyUpdated"),
                  showConfirmButton: false,
                  timer: 1500,
                  customClass: {
                    popup: 'success-banner',
                    title: 'success-banner-title'
                  },
                });
              }
              this.Get();
              this.closeUploaddialog();
            } else if (dataResponse.status == 3) {
              this.$swal.fire({
                icon: "warning",
                title: dataResponse.message,
                showConfirmButton: true,
              });
            }
            this.dialog = false;
          },
          (errorResource) => {
            this.dialog = false;
            this.Adddialog = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "DeleteGroup");
      }
    },

    GoToScore(id) {
      this.$router.push({
        name: "DashboardScore",
        query: {
          id: id,
        },
      });
    },
    Processing(id) {
      this.dialog = true;

      let request = {
        id: id,
        entity: this.Validation.entity,
      };

      try {
        this.$call.UploadValidationProcess(
          request,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              this.$swal.fire({
                icon: "success",
                title: dataResponse.message,
                showConfirmButton: false,
                timer: 1500,
              });
              this.closeUploaddialog();
              this.Get();
            }
            this.dialog = false;
          },
          (errorResource) => {
            this.dialog = false;
            this.Adddialog = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "DeleteGroup");
      }
    },
    resourceResult(result) {
      if (result != "") {
        let arr = result.split("/");
        if (arr.length == 2) {
          return "0/" + arr[1];
        }
      }
      return result;
    },

    Reprocess(source) {
      this.$swal
        .fire({
          title: this.$t("warning"),
          icon: "warning",
          // ถ้า run ยังทำงานอยู่ ต้องบอกให้ชัดว่ากดแล้วงานปัจจุบันจะถูกยกเลิก
          html: this.isRunning(source.status)
            ? `${this.$t("vectorReprocess")}<br><br><b>${this.$t(
                "reprocessRunningWarning"
              )}</b>`
            : this.$t("vectorReprocess"),
          showCloseButton: true,
          showCancelButton: true,
          cancelButtonText: this.$t("cancel"),
          confirmButtonText: this.$t("yes"),
          // ใช้ลำดับปุ่มเดียวกับกล่องยืนยันการลบ (reverseButtons: false) ไม่งั้นปุ่มปลอดภัย
          // กับปุ่มอันตรายจะสลับข้างกันระหว่างสองการกระทำที่อยู่ติดกันในแถวเดียว
          reverseButtons: false,
          // default focus ที่ปุ่มยกเลิก เพราะ reprocess จะยิง LLM ใหม่ทั้งชุด กด Enter พลาดแล้วย้อนไม่ได้
          focusCancel: true,
          buttonsStyling: false,
          allowOutsideClick: false,
          customClass: {
            popup: "swal-warning-popup",
            title: "swal-warning-title",
            htmlContainer: "swal-warning-body",
            actions: "swal-warning-actions",
            confirmButton: "swal-warning-confirm",
            cancelButton: "swal-warning-cancel",
            closeButton: "swal-warning-close",
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.dialog = true;
            if (source.status != "error:score") { // NOSONAR
              this.$call.ReprocessValidationProcess(
                source.processID,
                (dataResponse) => {
                  if (dataResponse.status == 0) {
                    source.status = "Reprocess";
                    source.result = this.resourceResult(source.result);
                    this.$swal.fire({
                      title: this.$t("_successfullyReprocessed"),
                      showConfirmButton: false,
                      timer: 1500,
                      customClass: {
                        popup: 'success-banner',
                        title: 'success-banner-title'
                      },
                    });
                    this.AutoRefresh();
                  }
                  this.dialog = false;
                },
                (errorResource) => {
                  this.dialog = false;
                  this.Adddialog = false;
                  console.error(errorResource);
                }
              );
            } else this.ProcessingScore(source);
          }
        });
    },
    ProcessingScore(source) {
      this.dialog = true;
      this.loading = true;
      let temp = {
        ProcessID: source.processID,
        UserID: this.userId,
      };
      this.$call.ReGenerateValidationScore(
        temp,
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.$swal.fire({
              icon: "success",
              title: dataResponse.message,
              showConfirmButton: false,
              timer: 1500,
            });
            // เดิมจบที่ toast เท่านั้น แถวยังโชว์ error:score ค้างอยู่จนกดรีเฟรชเอง
            // ทำให้ดูเหมือนกดไม่ติด ต้องดึงตารางใหม่แล้วปลุก polling เหมือน action อื่น
            this.Get();
          }
          this.dialog = false;
        },
        (errorResource) => {
          this.dialog = false;
          this.Adddialog = false;
          console.error(errorResource);
        }
      );
    },
    removeFile(fileName) {
      // Find the index of the
      const index = this.uploadedFiles.findIndex(
        (file) => file.name === fileName
      );
      // If file is in uploaded files remove it
      if (index > -1) this.uploadedFiles.splice(index, 1);
      this.$emit("filesUploaded", this.uploadedFiles);
      this.Validation.entity =
        this.uploadedFiles.length == 0 ? null : this.Validation.entity;
    },
    processUpload(e) {
      this.files = e;
      if (this.files.length == 0) {
        this.Validation.entity = null;
        return;
      }
      const reader = new FileReader();
      reader.readAsText(this.files[0]); // NOSONAR
      reader.onload = () => {
        let text = reader.result;
        this.ValidateCSVVector(text, this.files);
      };
    },
    ValidateCSVVector(csv, files) {
      const formFile = new FormData();
      formFile.append("formFile", files[0]); // key = "formFile" ตรงกับ parameter
      this.dialog = true;
      this.$call.ValidateCSV(
        formFile,
        (dataResponse) => {
          if (dataResponse.status == 0) {
            if (dataResponse.data) {
              this.$swal.fire({
                title: this.$t("_successfullyUploaded"),
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  popup: 'success-banner',
                  title: 'success-banner-title'
                },
              });

              const formDataDoc = new FormData();
              formDataDoc.append("entity", files[0]);
              this.uploadedFiles = files;
              this.Validation.entity = formDataDoc;
            } else {
              this.$swal.fire({
                title: this.$t("_UnsupportedFileFormat"),
                html: this.$t("_pleaseEnterCorrectFormatXlsx"),
                icon: "warning",
                showCloseButton: true,
                showCancelButton: false,
                confirmButtonText: `<span class="swal-delete-btn-content">${this.$t("ok")}</span>`,
                reverseButtons: false,
                focusCancel: true,
                buttonsStyling: false,
                customClass: {
                  popup: "swal-delete-popup",
                  title: "swal-delete-title",
                  htmlContainer: "swal-delete-body",
                  actions: "swal-delete-actions",
                  confirmButton: "swal-delete-confirm",
                },
              });
              this.Validation.entity = null;
              this.uploadedFiles = [];
            }
          } else {
            this.$swal.fire({
              icon: "error",
              title: dataResponse.message,
              showConfirmButton: true,
            });
          }
          this.dialog = false;
        },
        (errorResource) => {
          console.error(errorResource);
        }
      );
    },
    async downloadtemp() {
      const data = [
        {
          Questions: "จังหวัดที่อยู่เหนือสุดของประเทศไทย",
        },
        {
          Questions: "จังหวัดที่อยู่ใต้สุดของประเทศไทย",
        },
        {
          Questions: "จังหวัดที่มีพื้นที่มากที่สุดของประเทศไทย",
        },
      ];

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet1");

      // Add headers
      worksheet.columns = [
        { header: "Questions", key: "Questions", width: 30 },
      ];

      // Add rows
      data.forEach((item) => {
        worksheet.addRow(item);
      });

      // Style the header row
      worksheet.getRow(1).font = { bold: true };

      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();

      // Create a blob and download
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "upload_questions.xlsx";
      a.click();
      globalThis.URL.revokeObjectURL(url);
    },
    Download(id, fileName, isGroup) {
      this.dialog = true;
      this.loading = true;
      this.$call.DownloadCSVAnswer(
        // เดิม hardcode false ทั้งที่ปุ่มส่ง item.isGroup มาให้ → export แบบ chain เรียกจาก UI ไม่ได้เลย
        { id, isGroup: isGroup === true },
        (dataResponse) => {
          if (dataResponse) {
            let fileURL = globalThis.URL.createObjectURL(new Blob([dataResponse]));
            let fileLink = document.createElement("a");

            fileLink.href = fileURL;
            // backend คืน text/plain + .txt เมื่อ IsGroup = true (ValidationProcessController DownloadCSVAnswer)
            // ตอนที่ isGroup ยัง hardcode false อยู่ จะบังคับ .csv ก็ไม่มีปัญหา แต่พอส่งค่าจริงแล้ว
            // การเติม .csv ให้ไฟล์ transcript จะได้ไฟล์ที่เปิดด้วย Excel ไม่ได้เรื่อง
            const wantedExt = isGroup === true ? '.txt' : '.csv';
            if (!fileName.toLowerCase().endsWith(wantedExt)) {
              fileName += wantedExt;
            }
            fileLink.setAttribute("download", fileName);
            document.body.appendChild(fileLink);

            fileLink.click();
          }
          this.dialog = false;
          this.loading = false;
        },
        (errorResource) => {
          this.dialog = false;
          this.isSearch = false;
          this.loading = false;
          console.error(errorResource);
        }
      );
    },
    async chainTemplate() {
      const data = [
        ["Questions"],
        ["สวัสดีครับ"],
        ["Reset password ยังไง"],
        ["ระบบ AMACS"],
        ["#End#"],
        ["สวัสดีครับ"],
        ["ขอ NetworkID"],
        ["ระบบ UMS"],
      ];

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet1");

      // Add headers
      worksheet.columns = [
        { header: "Questions", key: "Questions", width: 50 },
        { header: "Human Answer", key: "Human Answer", width: 70 },
      ];

      // Add rows
      data.forEach((item) => {
        worksheet.addRow(item);
      });

      // Style the header row
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };

      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();

      // Create a blob and download
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Chain_template.xlsx";
      a.click();
      globalThis.URL.revokeObjectURL(url);
    },
    hasInProgressRow() {
      return (this.dataTable || []).some((row) => {
        if (!row || !this.isRunning(row.status)) return false;
        // อ่านความคืบหน้าไม่ได้ ให้ถือว่ายังทำงานอยู่ไว้ก่อน ปลอดภัยกว่าหยุด poll
        if (typeof row.result !== "string") return true;
        const totalSp = row.result.split("/");
        const done = Number.parseInt(totalSp[0], 10);
        const total = Number.parseInt(totalSp[1], 10);
        if (Number.isNaN(done) || Number.isNaN(total)) return true;
        // ยังไม่ทราบจำนวนคำถาม (กำลัง seed อยู่) — ช่วงนี้แหละที่ต้อง poll
        if (total <= 0) return true;
        // งานให้คะแนนบางเส้นทางไม่ reset counter ทำให้ done == total ตั้งแต่เริ่ม
        // ถ้าใช้เกณฑ์ done < total จะไม่ poll เลย ต้องดูที่สถานะอย่างเดียว
        if (row.status === "Processing score") return true;
        return done < total;
      });
    },
    // ปุ่ม Refresh บนหัวตาราง
    // เดิมเรียก ReFreshGet() เฉย ๆ ซึ่งดึงข้อมูลครั้งเดียวแต่ไม่ปลุก polling กลับมา
    // ถ้า AutoRefresh หยุดไปเพราะล้มติดกัน 3 ครั้ง ปุ่มนี้คือสิ่งแรกที่ผู้ใช้กด แต่มันไม่ช่วยอะไร
    // แถวที่ยังทำงานอยู่จะค้างไอคอนนาฬิกาไว้แม้ backend กลับมาปกติแล้ว
    async ManualRefresh() {
      await this.ReFreshGet();
      this.autoRefreshStopped = false;
      this.AutoRefresh();
    },
    async AutoRefresh() {
      // กัน polling loop ซ้อนกัน เพราะ Get() ถูกเรียกทุกครั้งที่เปลี่ยนหน้า ค้นหา หรือเรียงลำดับ
      if (this.autoRefreshRunning) return;
      this.autoRefreshRunning = true;
      let consecutiveFailures = 0;
      try {
        while (!this.autoRefreshStopped && this.hasInProgressRow()) {
          await new Promise((resolve) => setTimeout(resolve, 5000));
          if (this.autoRefreshStopped) break;
          // await เพื่อไม่ให้ request ซ้อนกันและตอบกลับสลับลำดับจนตัวเลขเด้งถอยหลัง
          const ok = await this.ReFreshGet();
          if (ok) {
            consecutiveFailures = 0;
            continue;
          }
          consecutiveFailures += 1;
          if (consecutiveFailures >= 3) {
            // apiPostAuth เด้ง Swal "Something went wrong!" ทุกครั้งที่ request ล้มเหลว
            // ถ้าไม่หยุด ผู้ใช้จะโดน popup ทุก 5 วินาทีไม่มีที่สิ้นสุด
            console.warn("ValidationProcess auto-refresh stopped after repeated failures");
            break;
          }
        }
      } finally {
        this.autoRefreshRunning = false;
      }
    },
    showPopup(id) {
      this.processID = null;
      if (id != null) { // NOSONAR
        this.processID = id;
        this.QuestionType = false;
        this.GetDetail(id);
      } else {
        this.QuestionType = true;
        this.Uploaddialog = true;
        this.Validation.validationName = null;
        this.Validation.modelId = null;
        this.Validation.Prompt = `You are an evaluator that scores the relevance of a chatbot's response based on the provided input.

If only the "Question" and "Bot's Response" are given, evaluate based solely on these two.
If a "Human's Response" is also provided, use it as an additional reference for scoring.
Scoring criteria:

0: Not related at all
1: Somewhat related
2: Very related
Evaluate the following input and respond only with a single number (0, 1, or 2). Do not include any explanation or additional text.

Provided data:

Question: {Question}
Bot's Response: {Bot’s Response}
Human's Response (if available): {Human’s Response}
Your response must be only a single number (0, 1, or 2).`;
      }
    },
    DeleteProcessPopup(Id, status) {
      try {
        this.$swal
          .fire({
            title: this.$t("confirmDeletion"),
            // การลบ run ที่ยังทำงานอยู่ จะยกเลิกงานที่วิ่งอยู่ด้วย ต้องบอกผู้ใช้ให้ชัด
            html: this.isRunning(status)
              ? `${this.$t("_confirmDeleteValidation")}<br><br><b>${this.$t(
                  "reprocessRunningWarning"
                )}</b>`
              : this.$t("_confirmDeleteValidation"),
            icon: "warning",
            showCloseButton: true,
            showCancelButton: true,
            cancelButtonText: this.$t("cancel"),
            confirmButtonText: `<span class="swal-delete-btn-content"><img src="${deleteIcon}" alt="" class="swal-delete-btn-icon" />${this.$t("delete")}</span>`,
            reverseButtons: false,
            focusCancel: true,
            buttonsStyling: false,
            customClass: {
              popup: "swal-delete-popup",
              title: "swal-delete-title",
              htmlContainer: "swal-delete-body",
              actions: "swal-delete-actions",
              confirmButton: "swal-delete-confirm",
              cancelButton: "swal-delete-cancel",
            },
          })
          .then((result) => {
            if (result.isConfirmed) {
              this.dialog = true;
              this.$call.DeleteValidationProcess(
                Id,
                (dataResponse) => {
                  if (dataResponse.status == 0) {
                    this.$swal.fire({
                      title: this.$t("_successfullySaved"),
                      showConfirmButton: false,
                      timer: 1500,
                      customClass: {
                        popup: 'success-banner',
                        title: 'success-banner-title'
                      },
                    });
                  }
                  this.dialog = false;
                  this.Get();
                },
                (errorResource) => {
                  this.dialog = false;
                  this.isSearch = false;
                  this.$swal.fire({
                    title: this.$t("error"),
                    text: this.$t("_somethingWentWrong"),
                    icon: "warning",
                  });
                  console.error(errorResource);
                }
              );
            }
          });
      } catch (error) {
        this.LogTrace(error, "DeleteGroup");
      }
    },

    showWarningAlert(title, messageHtml) {
      const rawOkLabel = this.$t ? this.$t("ok") : "OK";
      const confirmLabel = rawOkLabel && rawOkLabel.toString().trim().length > 0
        ? rawOkLabel
        : "OK";

      return this.$swal.fire({
        title,
        html: messageHtml,
        icon: "warning",
        showCloseButton: true,
        focusConfirm: true,
        buttonsStyling: false,
        allowOutsideClick: false,
        confirmButtonText: confirmLabel,
        customClass: {
          popup: "swal-warning-popup",
          title: "swal-warning-title",
          htmlContainer: "swal-warning-body",
          actions: "swal-warning-actions",
          confirmButton: "swal-warning-confirm",
          closeButton: "swal-warning-close",
        },
      });
    },
  },
  beforeUnmount() {
    // หยุด polling loop เมื่อออกจากหน้า ไม่งั้น loop จะวิ่งต่อไปเรื่อย ๆ หลัง component ถูกทำลาย
    // เดิมเทียบ window.location.pathname กับ "/ValidationProcess" ตรง ๆ ซึ่งพังทันทีถ้า deploy
    // ใต้ sub-path (เช่น /chat/ValidationProcess) — loop จะหยุดทั้งที่ผู้ใช้ยังอยู่หน้านี้
    this.autoRefreshStopped = true;
  },
  deactivated() {
    // เผื่อกรณีถูกครอบด้วย keep-alive ซึ่งจะไม่เรียก beforeUnmount
    this.autoRefreshStopped = true;
  },
  activated() {
    this.autoRefreshStopped = false;
    this.AutoRefresh();
  },
  mounted() {
    this.GetDate();
    this.GetDDGPTModel();
  },
};
</script>
<style scoped>
textarea {
  min-height: 800px !important;
}

.flex {
  margin-left: 2rem;
  margin-right: 2rem;
}

:deep(.UploadFile .NoDragDrop button.v-btn) {
  margin: 0px !important;
  width: 100% !important;
  border-radius: 10px !important;
  font-weight: 500 !important;
}

.modern-dialog {
  margin: 24px;
  animation: dialogSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2) !important;
}

/* Dialog Content */
.dialog-content {
  padding: 20px 24px !important;
  background: #ffffff;
  max-height: 70vh;
  overflow-y: auto;
}

.field-container {
  margin-bottom: 0px;
}

.field-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  display: block;
  letter-spacing: 0.25px;
}

/* Modern field and textarea shared styles */
.modern-field :deep(.v-field__outline),
.modern-textarea :deep(.v-field__outline) {
  border-radius: 12px !important;
  border-width: 2px !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
  transition: all 0.3s ease !important;
}

.modern-field :deep(.v-field--focused .v-field__outline),
.modern-textarea :deep(.v-field--focused .v-field__outline) {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1) !important;
}

.modern-field :deep(.v-field),
.modern-textarea :deep(.v-field) {
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  transition: all 0.3s ease !important;
}

.modern-field:hover :deep(.v-field__outline),
.modern-textarea:hover :deep(.v-field__outline) {
  border-color: rgba(25, 118, 210, 0.6) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.modern-field :deep(.v-label),
.modern-textarea :deep(.v-label) {
  font-weight: 600 !important;
  color: #495057 !important;
  font-size: 0.875rem !important;
  letter-spacing: 0.25px !important;
}

.modern-field :deep(.v-field--focused .v-label),
.modern-textarea :deep(.v-field--focused .v-label) {
  color: #1976d2 !important;
}

.modern-field :deep(.v-field__input),
.modern-textarea :deep(.v-field__input) {
  font-size: 1rem !important;
  font-weight: 500 !important;
  color: #333 !important;
  padding: 16px 12px !important;
}

.modern-field :deep(.v-field__input::placeholder),
.modern-textarea :deep(.v-field__input::placeholder) {
  color: #adb5bd !important;
  font-style: italic;
}

/* Radio Group Styles */
.modern-radio-group {
  margin-top: 8px !important;
}

.radio-container {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.modern-radio :deep(.v-selection-control) {
  background: rgba(248, 249, 250, 0.8);
  border-radius: 12px;
  padding: 12px 16px;
  margin: 4px 0;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.modern-radio :deep(.v-selection-control:hover) {
  background: rgba(248, 249, 250, 1);
  border-color: rgba(25, 118, 210, 0.2);
}

.modern-radio :deep(.v-selection-control--dirty) {
  border-color: currentColor;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.radio-label-wrapper {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #495057;
}

/* Checkbox Styles */
.modern-checkbox :deep(.v-selection-control) {
  background: rgba(248, 249, 250, 0.8);
  border-radius: 12px;
  padding: 12px 16px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.modern-checkbox :deep(.v-selection-control:hover) {
  background: rgba(248, 249, 250, 1);
  border-color: rgba(25, 118, 210, 0.2);
}

.modern-checkbox :deep(.v-selection-control--dirty) {
  border-color: rgba(25, 118, 210, 0.3);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.checkbox-label-wrapper {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #495057;
}

/* Files Display */
.files-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  transition: all 0.2s ease;
}

.files-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.3);
  box-shadow: 0 4px 12px rgba(var(--v-theme-on-surface), 0.08);
}

.files-card .v-card-title {
  background: rgba(var(--v-theme-surface-variant), 0.3);
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.file-item {
  transition: background-color 0.2s ease;
}

.file-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.file-item .v-btn:hover {
  background-color: rgba(var(--v-theme-error), 0.1) !important;
}

/* Template Download Button */
.template-download-btn {
  border-radius: 10px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  transition: all 0.3s ease !important;
}

.template-download-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(var(--v-theme-info), 0.3) !important;
}

/* Dialog Actions */
.dialog-actions {
  background: rgba(248, 249, 250, 0.5) !important;
  padding: 20px 24px !important;
  gap: 12px;
}

.cancel-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  min-width: 120px !important;
  border-color: #dee2e6 !important;
  color: #6c757d !important;
}

.cancel-btn:hover {
  background: #f8f9fa !important;
  border-color: #adb5bd !important;
  color: #495057 !important;
}

.save-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  min-width: 120px !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;
  transition: all 0.3s ease !important;
}

.save-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(25, 118, 210, 0.4) !important;
}

.save-edit-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  min-width: 120px !important;
  box-shadow: 0 4px 12px rgba(255, 164, 7, 0.3) !important;
  transition: all 0.3s ease !important;
}

.save-edit-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(255, 164, 7, 0.4) !important;
}

/* Upload File Component Integration */
.UploadFile {
  width: 100%;
}

.score-btn {
  border-radius: 50% !important;
  width: 45px !important;
  height: 45px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  background: linear-gradient(135deg, #3859D0 0%, #BD6AD5 127.43%) !important;
  box-shadow: 0 4px 12px rgba(156, 39, 176, 0.3) !important;
}

.score-btn:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.05) !important;
  box-shadow: 0 8px 20px rgba(156, 39, 176, 0.4) !important;
  background: linear-gradient(135deg, #ab47bc 0%, #7e57c2 100%) !important;
}

.score-btn:active:not(:disabled) {
  transform: translateY(0) scale(1) !important;
}

.score-btn:disabled {
  background: #e0e0e0 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  cursor: not-allowed !important;
  transform: none !important;
  opacity: 0.6 !important;
}

.score-btn .v-icon {
  transition: transform 0.2s ease !important;
}

.score-btn:hover:not(:disabled) .v-icon {
  transform: scale(1.1) rotate(5deg) !important;
}

/* Pulse animation for active state */
.score-btn:not(:disabled) {
  position: relative;
  background-size: 200% 100%;
  animation: shimmer 3s infinite;
}

.score-btn:not(:disabled)::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(156, 39, 176, 0.3);
  transform: translate(-50%, -50%) scale(0);
  transition: transform 0.3s ease;
}

.score-btn:hover:not(:disabled)::before {
  transform: translate(-50%, -50%) scale(1.3);
  opacity: 0;
}

/* Shimmer effect for successful state */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

/* Enhanced tooltip styling */
.score-btn .v-tooltip {
  font-size: 0.875rem !important;
  font-weight: 500 !important;
}

/* =============================================================================
   RESPONSIVE DESIGN FOR SCORE BUTTON
   ============================================================================= */
@media (max-width: 768px) {
  .score-btn {
    width: 40px !important;
    height: 40px !important;
  }

  .score-btn .v-icon {
    font-size: 20px !important;
  }
}

/* =============================================================================
   ACTION BUTTONS GROUP ENHANCEMENT
   ============================================================================= */
.action-buttons-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.action-btn {
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
  min-width: 32px !important;
  width: 32px !important;
  height: 32px !important;
}

.action-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

.edit-btn:hover {
  background-color: rgba(var(--v-theme-primary), 0.1) !important;
}

.reprocess-btn:hover {
  background-color: rgba(var(--v-theme-info), 0.1) !important;
}

.download-btn:hover:not(:disabled) {
  background-color: rgba(var(--v-theme-success), 0.1) !important;
}

.download-btn:disabled {
  background-color: rgba(0, 160, 35, 0.295) !important;
  cursor: not-allowed !important;
  opacity: 0.6 !important;
}

.delete-btn:hover {
  background-color: rgba(var(--v-theme-error), 0.1) !important;
}

/* Rotating animation for reprocess icon */
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.rotating {
  animation: rotate 1s linear infinite;
}

/* Hover effects for icons */
.action-btn .v-icon {
  transition: transform 0.2s ease !important;
}

.action-btn:hover .v-icon {
  transform: scale(1.1) !important;
}

/* Disabled state styling */
.action-btn:disabled {
  cursor: not-allowed !important;
}

.action-btn:disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}

/* Action divider */
.action-divider {
  width: 1px;
  height: 24px;
  background: rgba(0, 0, 0, 0.12);
  margin: 0 4px;
}

/* Animations */
@keyframes dialogSlideIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .dialog-header {
    padding: 20px 16px !important;
  }

  .dialog-title {
    font-size: 1.25rem !important;
  }

  .dialog-content {
    padding: 24px 16px !important;
  }

  .dialog-actions {
    padding: 16px !important;
    flex-direction: column;
  }

  .cancel-btn,
  .save-btn,
  .save-edit-btn {
    width: 100% !important;
    min-width: auto !important;
  }

  .radio-container {
    flex-direction: column;
    gap: 8px;
  }
}

.action-controls {
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  padding: 4px 8px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  min-width: 200px;
}

.action-controls:hover {
  background: rgba(0, 0, 0, 0.03);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

:deep(.v-data-table tbody tr) {
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.v-data-table tbody tr:hover) {
  background: rgba(0, 0, 0, 0.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

:deep(.v-data-table-header) {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 2px solid #dee2e6;
}

:deep(.v-data-table-header th) {
  color: #495057 !important;
  font-weight: 700 !important;
  font-size: 0.875rem !important;
  padding: 1rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid rgba(25, 118, 210, 0.1) !important;
}


/* action disable button delete */
.delete-btn:disabled {
  opacity: 0.4 !important;
  cursor: not-allowed !important;
  background-color: rgba(236, 15, 15, 0.304) !important;
  pointer-events: none !important;
}

.delete-btn:disabled .v-icon {
  color: rgb(253, 19, 3) !important;
}

.delete-btn:disabled:hover {
  transform: none !important;
  box-shadow: none !important;
  background-color: rgba(244, 67, 54, 0.1) !important;
}

.edit-btn:disabled {
  opacity: 0.4 !important;
  cursor: not-allowed !important;
  background-color: rgba(245, 193, 37, 0.686) !important;
  pointer-events: none !important;
}

.edit-btn:disabled .v-icon {
  color: rgb(155, 122, 31) !important;
}
</style>

<!-- Redesign batch css -->
<style scoped>
.main-page-div {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.redesign-main-page-container {
  padding: 24px;
}

.main-content-card {
  border-radius: 16px !important;
  border: 1px solid #DCDCDC !important;
  background: #FFF !important;
  overflow: hidden;
  padding: 20px;
}

.reportdata-icon-btn {
  border-radius: 50%;
  height: 42px;
  width: 42px;
  min-width: 42px;
  transform: scaleX(-1);
}

.reportdata-icon-btn--ghost {
  background: #ffffff !important;
  color: #171717 !important;
  border: 1px solid #d0d0d0 !important;
}

/* table base */
:deep(thead) {
  background-color: #FFF2F2 !important;
  padding: 0 8px;
}

.v-table--has-top :deep(.v-table__wrapper) {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

:deep(.v-data-table .v-table__wrapper > table > thead > tr th.v-data-table-column--align-left .v-data-table-header__content span) {
  font-weight: 600;
  font-size: 14px;
}

:deep(.v-data-table .v-table__wrapper > table > thead > tr th.v-data-table-column--align-center .v-data-table-header__content span) {
  font-weight: 600;
  font-size: 14px;
}

/* table */

.manage-image-icon {
  width: 25px;
  height: 25px;
}

.chip-processing {
  border-radius: 100px;
  border: 2px dashed var(--Royal-blue, #001763);
  background: #EABD07 !important;
  color: #001763 !important;
}

.chip-label {
  color: inherit;
}

.image-icon-style {
  width: 24px;
  height: 24px;
  margin-right: 8px;
  flex-shrink: 0;
  object-fit: contain;
}

/* New Dialog Redesign Styles */
.input-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.87;
  line-height: 1.2;
  margin-bottom: 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.info-icon {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.info-icon:hover {
  opacity: 1;
}

/* Modern Field Styles - Matching DashboardScore */
.modern-field {
  margin-top: 0px;
}

.modern-field :deep(.v-field) {
  border-radius: 16px !important;
  background: #ffffff !important;
  box-shadow: unset !important;
  transition: all 0.3s ease !important;
}

.modern-field :deep(.v-field__outline) {
  border-width: 2px !important;
  border-color: #e0e0e0 !important;
  transition: all 0.3s ease !important;
}

.modern-field :deep(.v-field--focused .v-field__outline) {
  border-color: #e0e0e0 !important;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1) !important;
}

.modern-field:hover :deep(.v-field__outline) {
  border-color: #e0e0e0 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.modern-field :deep(.v-field__input) {
  font-size: 1rem !important;
  font-weight: 500 !important;
  color: #333 !important;
  padding: 5px 10px !important;
}

.modern-field :deep(.v-field__input::placeholder) {
  color: #adb5bd !important;
  font-style: italic;
}

/* Modern Textarea */
.modern-textarea {
  margin-top: 0px;
}

.modern-textarea :deep(.v-field) {
  border-radius: 16px !important;
  background: #ffffff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  transition: all 0.3s ease !important;
}

.modern-textarea :deep(.v-field__outline) {
  border-width: 2px !important;
  border-color: #e0e0e0 !important;
  transition: all 0.3s ease !important;
}

.modern-textarea :deep(.v-field--focused .v-field__outline) {
  border-color: #e0e0e0 !important;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1) !important;
}

.modern-textarea:hover :deep(.v-field__outline) {
  border-color: #e0e0e0 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.modern-textarea :deep(.v-field__input) {
  font-size: 1rem !important;
  font-weight: 500 !important;
  color: #333 !important;
  padding: 16px 12px !important;
}

/* Custom Radio Group Styles */
.custom-radio-group {
  margin-top: 0 !important;
}

.custom-radio-group :deep(.v-selection-control) {
  min-height: auto !important;
  margin-right: 24px;
}

.custom-radio {
  margin-right: 24px;
}

.custom-radio :deep(.v-selection-control__wrapper) {
  height: 20px;
}

.custom-radio :deep(.v-selection-control__input) {
  width: 20px;
  height: 20px;
}

/* CRITICAL: Remove ALL default Vuetify backgrounds */
.custom-radio :deep(.v-selection-control__input::before),
.custom-radio :deep(.v-selection-control__input::after) {
  content: none !important;
  display: none !important;
  background: none !important;
  background-color: transparent !important;
  opacity: 0 !important;
  visibility: hidden !important;
}

.custom-radio :deep(.v-selection-control__input .v-icon) {
  font-size: 20px !important;
  color: #C32E0E !important;
}

/* Unchecked state - Red circle outline */
.custom-radio :deep(.v-selection-control__input .mdi-radiobox-blank) {
  color: #C32E0E !important;
  opacity: 0.6 !important;
}

/* Checked state - Red filled circle */
.custom-radio :deep(.v-selection-control--dirty .v-selection-control__input .mdi-radiobox-marked) {
  color: #C32E0E !important;
  opacity: 1 !important;
}

/* Force override any inherited color */
.custom-radio :deep(.v-selection-control--dirty .v-selection-control__input) {
  color: #C32E0E !important;
}

.custom-radio :deep(.v-label) {
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  color: #495057 !important;
  opacity: 1 !important;
}

/* Upload Card */
.upload-card {
  border-radius: 16px;
  border: 2px dashed #949AA8 !important;
  background: linear-gradient(90deg, #FFF2F2 -15.8%, #FFF 100%) !important;
  transition: all 0.3s ease;
  min-height: 120px;
}

.upload-card:hover {
  border-color: #949AA8 !important;
  background: #FFF2F0 !important;
}

/* Border Section Wrapper for Checkbox */
.border-section-wrapper {
  border-radius: 24px;
  padding: 15px 10px;
  border: 1px solid #a0a0a0;
  transition: all 0.3s ease;
  width: 100% !important;
}

.checkbox-label-wrapper {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #495057;
}

/* Template Button */
.template-btn {
  min-height: 35px !important;
  border-radius: 12px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  transition: all 0.3s ease !important;
  border-color: #C32E0E !important;
  color: #C32E0E !important;
}

.template-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(195, 46, 14, 0.3) !important;
  background: rgba(195, 46, 14, 0.04) !important;
}
/* Modern Autocomplete/Select Dropdown Arrow - Red Color */
.modern-field :deep(.v-field__append-inner .v-icon) {
  color: #872612 !important;
}

.modern-field :deep(.v-autocomplete__menu-icon) {
  color: #872612 !important;
}

/* Question Type chip ในตาราง */
.meta-chip {
  max-width: 180px;
  font-weight: 500 !important;
}

/* ชื่อโมเดล/ชื่อชุดคำถามยาวได้ จึงตัดด้วย ellipsis แล้วให้ดูเต็มจาก title */
.table-cell-text {
  display: inline-block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>